import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Avatar,
  Alert,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Search,
  MoreVert,
  Visibility,
  CheckCircle,
  Block,
  Star,
  DriveEta,
  AttachMoney,
  VerifiedUser,
  Warning
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

interface Driver {
  id: string;
  email: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  status: string;
  driverStatus?: string;
  phoneVerified: boolean;
  emailVerified: boolean;
  driverRating: number;
  totalTripsAsDriver: number;
  createdAt: string;
  profilePicture?: string;
  documents?: any[];
  vehicles?: any[];
}

interface DriverDetailsData extends Driver {
  ridesAsDriver?: any[];
  documents?: any[];
  vehicles?: any[];
}

const DriversPage: React.FC = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalDrivers, setTotalDrivers] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [driverDetails, setDriverDetails] = useState<DriverDetailsData | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  // New dialogs for CRUD operations
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [editDialog, setEditDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [permanentDeleteDialog, setPermanentDeleteDialog] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    email: '',
    phoneNumber: '',
    password: '',
    role: 'DRIVER',
    status: 'ACTIVE'
  });

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  useEffect(() => {
    fetchDrivers();
  }, [page, rowsPerPage, search, statusFilter, verificationFilter, ratingFilter]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        role: 'DRIVER', // Only fetch drivers
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter })
      });

      const response = await axios.get(`${apiUrl}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      let filteredDrivers = response.data.users;

      // Client-side filtering for rating (if backend doesn't support it)
      if (ratingFilter) {
        const minRating = parseFloat(ratingFilter);
        filteredDrivers = filteredDrivers.filter((d: Driver) =>
          (d.driverRating || 0) >= minRating
        );
      }

      setDrivers(filteredDrivers);
      setTotalDrivers(response.data.pagination.total);
    } catch (error: any) {
      console.error('Failed to fetch drivers:', error);
      if (error?.response) {
        const status = error.response.status;
        const message = error.response?.data?.message || 'Failed to load drivers';
        if (status === 401 || status === 403) {
          showAlert('error', 'Session expired. Please log in again.');
          // Clear and redirect after a brief delay so the user sees the message
          setTimeout(() => {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminInfo');
            navigate('/KP2lPQ25klr/login');
          }, 800);
        } else if (status === 429) {
          showAlert('error', 'Too many requests. Please slow down and try again shortly.');
        } else if (status >= 500) {
          showAlert('error', `Server error: ${message}`);
        } else {
          showAlert('error', message);
        }
      } else if (error?.request) {
        showAlert('error', 'Cannot reach API server. Please check network or server status.');
      } else {
        showAlert('error', 'Unexpected error occurred while loading drivers.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverDetails = async (driverId: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/users/${driverId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDriverDetails(response.data.user);
      setDetailsDialog(true);
    } catch (error) {
      console.error('Failed to fetch driver details:', error);
      showAlert('error', 'Failed to load driver details');
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, driver: Driver) => {
    setAnchorEl(event.currentTarget);
    setSelectedDriver(driver);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!selectedDriver) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${apiUrl}/admin/users/${selectedDriver.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', `Driver status updated to ${newStatus}`);
      handleMenuClose();
      fetchDrivers();
    } catch (error: any) {
      console.error('Failed to update status:', error);
      showAlert('error', error.response?.data?.message || 'Failed to update driver status');
    }
  };

  const showAlert = (type: 'success' | 'error', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  // Open status change dialog
  const handleOpenStatusDialog = () => {
    if (!selectedDriver) return;
    setNewStatus(selectedDriver.status);
    setStatusReason('');
    setStatusDialog(true);
    handleMenuClose();
  };

  // Handle status change submission
  const handleStatusChange = async () => {
    if (!selectedDriver) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${apiUrl}/admin/users/${selectedDriver.id}/status`,
        { status: newStatus, reason: statusReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', `Driver status updated to ${newStatus}`);
      setStatusDialog(false);
      fetchDrivers();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to update status');
    }
  };

  // Open edit dialog
  const handleOpenEditDialog = () => {
    if (!selectedDriver) return;
    setFormData({
      firstName: selectedDriver.firstName,
      lastName: selectedDriver.lastName,
      preferredName: selectedDriver.preferredName || '',
      email: selectedDriver.email,
      phoneNumber: selectedDriver.phoneNumber,
      password: '',
      role: 'DRIVER',
      status: selectedDriver.status
    });
    setEditDialog(true);
    handleMenuClose();
  };

  // Handle edit driver
  const handleEditDriver = async () => {
    if (!selectedDriver) return;

    try {
      const token = localStorage.getItem('adminToken');
      const updateData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        status: formData.status
      };

      await axios.put(
        `${apiUrl}/admin/users/${selectedDriver.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Driver updated successfully');
      setEditDialog(false);
      fetchDrivers();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to update driver');
    }
  };

  // Open create dialog
  const handleOpenCreateDialog = () => {
    setFormData({
      firstName: '',
      lastName: '',
      preferredName: '',
      email: '',
      phoneNumber: '',
      password: '',
      role: 'DRIVER',
      status: 'ACTIVE'
    });
    setCreateDialog(true);
  };

  // Handle create driver
  const handleCreateDriver = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${apiUrl}/admin/users`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Driver created successfully');
      setCreateDialog(false);
      fetchDrivers();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to create driver');
    }
  };

  // Open delete dialog (soft delete)
  const handleOpenDeleteDialog = () => {
    setDeleteDialog(true);
    handleMenuClose();
  };

  // Handle soft delete (ban)
  const handleDeleteDriver = async () => {
    if (!selectedDriver) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${apiUrl}/admin/users/${selectedDriver.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Driver has been banned');
      setDeleteDialog(false);
      fetchDrivers();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to ban driver');
    }
  };

  // Open permanent delete dialog
  const handleOpenPermanentDeleteDialog = () => {
    setPermanentDeleteDialog(true);
    handleMenuClose();
  };

  // Handle permanent delete (GDPR)
  const handlePermanentDelete = async () => {
    if (!selectedDriver) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${apiUrl}/admin/users/${selectedDriver.id}/permanent`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Driver account permanently deleted');
      setPermanentDeleteDialog(false);
      fetchDrivers();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to permanently delete driver');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING_VERIFICATION': return 'warning';
      case 'SUSPENDED': return 'error';
      case 'BANNED': return 'error';
      default: return 'default';
    }
  };

  const getVerificationStatus = (driver: Driver) => {
    if (driver.driverStatus) {
      switch (driver.driverStatus) {
        case 'APPROVED':
          return { status: 'Verified', color: 'success' as const, chipColor: 'success' as const, iconColor: 'success' as const, icon: <VerifiedUser /> };
        case 'REJECTED':
          return { status: 'Rejected', color: 'error' as const, chipColor: 'error' as const, iconColor: 'error' as const, icon: <Block /> };
        case 'PENDING':
        case 'AI_VERIFIED':
          return { status: 'Pending', color: 'warning' as const, chipColor: 'warning' as const, iconColor: 'warning' as const, icon: <Warning /> };
        case 'NOT_APPLIED':
          return { status: 'Not Applied', color: 'info' as const, chipColor: 'default' as const, iconColor: 'action' as const, icon: <Warning /> };
        default:
          break;
      }
    }

    if (!driver.documents || driver.documents.length === 0) {
      return { status: 'No Documents', color: 'error' as const, chipColor: 'error' as const, iconColor: 'error' as const, icon: <Warning /> };
    }

    const allApproved = driver.documents.every(doc => doc.status === 'APPROVED');
    const anyPending = driver.documents.some(doc => doc.status === 'PENDING');
    const anyRejected = driver.documents.some(doc => doc.status === 'REJECTED');

    if (allApproved) {
      return { status: 'Verified', color: 'success' as const, chipColor: 'success' as const, iconColor: 'success' as const, icon: <VerifiedUser /> };
    } else if (anyRejected) {
      return { status: 'Rejected', color: 'error' as const, chipColor: 'error' as const, iconColor: 'error' as const, icon: <Block /> };
    } else if (anyPending) {
      return { status: 'Pending', color: 'warning' as const, chipColor: 'warning' as const, iconColor: 'warning' as const, icon: <Warning /> };
    }
    return { status: 'Incomplete', color: 'primary' as const, chipColor: 'default' as const, iconColor: 'action' as const, icon: <Warning /> };
  };

  const getVerificationProgress = (driver: Driver) => {
    if (!driver.documents || driver.documents.length === 0) return 0;
    const approved = driver.documents.filter(doc => doc.status === 'APPROVED').length;
    const total = driver.documents.length;
    return (approved / total) * 100;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Driver Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage drivers, verify documents, and monitor performance
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenCreateDialog}
          sx={{ textTransform: 'none' }}
        >
          + Add New Driver
        </Button>
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' }, gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="PENDING_VERIFICATION">Pending Verification</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
              <MenuItem value="BANNED">Banned</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Rating</InputLabel>
            <Select
              value={ratingFilter}
              label="Rating"
              onChange={(e) => setRatingFilter(e.target.value)}
            >
              <MenuItem value="">All Ratings</MenuItem>
              <MenuItem value="4.5">4.5+ Stars</MenuItem>
              <MenuItem value="4.0">4.0+ Stars</MenuItem>
              <MenuItem value="3.5">3.5+ Stars</MenuItem>
              <MenuItem value="3.0">3.0+ Stars</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => {
            setSearch('');
            setStatusFilter('');
            setVerificationFilter('');
            setRatingFilter('');
          }}>
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Drivers Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Driver</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Verification</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Total Rides</TableCell>
                <TableCell>Vehicles</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : drivers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      No drivers found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                drivers.map((driver) => {
                  const verification = getVerificationStatus(driver);
                  const progress = getVerificationProgress(driver);

                  return (
                    <TableRow key={driver.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar src={driver.profilePicture}>
                            {driver.firstName[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {driver.preferredName || driver.firstName} {driver.lastName ? driver.lastName.charAt(0) + '.' : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {driver.id.substring(0, 8)}...
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{driver.email}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {driver.phoneNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={driver.status} size="small" color={getStatusColor(driver.status)} />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Chip
                            icon={verification.icon}
                            label={verification.status}
                            size="small"
                            color={verification.chipColor}
                          />
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ mt: 1, height: 4, borderRadius: 2 }}
                            color={verification.color}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star sx={{ fontSize: 16, color: 'gold' }} />
                          <Typography variant="body2" fontWeight="medium">
                            {driver.driverRating?.toFixed(1) || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {driver.totalTripsAsDriver}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {driver.vehicles?.length || 0} vehicle{driver.vehicles?.length !== 1 ? 's' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => fetchDriverDetails(driver.id)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, driver)}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalDrivers}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedDriver) fetchDriverDetails(selectedDriver.id);
          handleMenuClose();
        }}>
          <Visibility fontSize="small" sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem onClick={handleOpenEditDialog}>
          Edit Driver
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleOpenStatusDialog}>
          Change Status
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleOpenDeleteDialog} sx={{ color: 'warning.main' }}>
          Ban Driver
        </MenuItem>
        <MenuItem onClick={handleOpenPermanentDeleteDialog} sx={{ color: 'error.main' }}>
          Permanent Delete
        </MenuItem>
      </Menu>

      {/* Driver Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Driver Details
        </DialogTitle>
        <DialogContent>
          {driverDetails && (
            <Box sx={{ pt: 2 }}>
              {/* Driver Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar src={driverDetails.profilePicture} sx={{ width: 80, height: 80 }}>
                  {driverDetails.firstName[0]}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">
                    {driverDetails.preferredName || driverDetails.firstName} {driverDetails.lastName ? driverDetails.lastName.charAt(0) + '.' : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {driverDetails.email} • {driverDetails.phoneNumber}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip label={driverDetails.status} size="small" color={getStatusColor(driverDetails.status)} />
                    <Chip
                      icon={<Star sx={{ fontSize: 16 }} />}
                      label={`${driverDetails.driverRating?.toFixed(1) || 'N/A'} Rating`}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Statistics */}
              <Typography variant="h6" gutterBottom>Statistics</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <DriveEta color="primary" />
                  <Typography variant="h5" fontWeight="bold">{driverDetails.totalTripsAsDriver}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Rides</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Star sx={{ color: 'gold' }} />
                  <Typography variant="h5" fontWeight="bold">{driverDetails.driverRating?.toFixed(1) || 'N/A'}</Typography>
                  <Typography variant="caption" color="text.secondary">Average Rating</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <VerifiedUser color={getVerificationStatus(driverDetails).iconColor} />
                  <Typography variant="h5" fontWeight="bold">{getVerificationProgress(driverDetails).toFixed(0)}%</Typography>
                  <Typography variant="caption" color="text.secondary">Verified</Typography>
                </Paper>
              </Box>

              {/* Vehicles */}
              <Typography variant="h6" gutterBottom>Vehicles</Typography>
              {driverDetails.vehicles && driverDetails.vehicles.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                  {driverDetails.vehicles.map((vehicle: any) => (
                    <Paper key={vehicle.id} sx={{ p: 2 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {vehicle.make?.name || 'Unknown Make'} {vehicle.model?.name || 'Unknown Model'} ({vehicle.year})
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Color: {vehicle.color}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        License Plate: {vehicle.licensePlate}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Type: {vehicle.type || 'N/A'} • Seats: {vehicle.seats}
                      </Typography>
                      {vehicle.features && vehicle.features.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                          Features: {vehicle.features.join(', ')}
                      </Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 3 }}>No vehicles registered</Alert>
              )}

              {/* Documents */}
              <Typography variant="h6" gutterBottom>Verification Documents</Typography>
              {driverDetails.documents && driverDetails.documents.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {driverDetails.documents.map((doc: any) => {
                    const type = (doc.type || doc.documentType || '').toString();
                    return (
                      <Paper key={doc.id} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {type.replace(/_/g, ' ')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Chip label={doc.status} size="small" color={getStatusColor(doc.status)} />
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
                <Alert severity="warning">No documents uploaded</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)}>
        <DialogTitle>Change Driver Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="PENDING_VERIFICATION">Pending Verification</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
              <MenuItem value="BANNED">Banned</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Reason (optional)"
            multiline
            rows={3}
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
          <Button onClick={handleStatusChange} variant="contained">Update Status</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Driver Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)}>
        <DialogTitle>Edit Driver</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="First Name"
            value={formData.preferredName || formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Phone Number"
            value={formData.phoneNumber}
            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="PENDING_VERIFICATION">Pending Verification</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
              <MenuItem value="BANNED">Banned</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditDriver} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Create Driver Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)}>
        <DialogTitle>Create New Driver</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="First Name"
            value={formData.preferredName || formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Phone Number"
            value={formData.phoneNumber}
            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="PENDING_VERIFICATION">Pending Verification</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateDriver} variant="contained">Create Driver</Button>
        </DialogActions>
      </Dialog>

      {/* Delete (Ban) Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Ban Driver</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will ban the driver. They will not be able to access their account.
          </Alert>
          <Typography>
            Are you sure you want to ban <strong>{selectedDriver?.preferredName || selectedDriver?.firstName} {selectedDriver?.lastName ? selectedDriver?.lastName.charAt(0) + '.' : ''}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteDriver} variant="contained" color="warning">
            Ban Driver
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent Delete Dialog */}
      <Dialog open={permanentDeleteDialog} onClose={() => setPermanentDeleteDialog(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>⚠️ Permanently Delete Account</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>WARNING: This action cannot be undone!</strong>
          </Alert>
          <Typography sx={{ mb: 2 }}>
            This will <strong>permanently remove all driver data</strong> including:
          </Typography>
          <Box component="ul" sx={{ mb: 2 }}>
            <li>Profile information</li>
            <li>All rides offered</li>
            <li>Vehicles</li>
            <li>Documents</li>
            <li>Payment records</li>
            <li>Messages and reviews</li>
          </Box>
          <Typography>
            Are you sure you want to permanently delete <strong>{selectedDriver?.preferredName || selectedDriver?.firstName} {selectedDriver?.lastName ? selectedDriver?.lastName.charAt(0) + '.' : ''}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermanentDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handlePermanentDelete} variant="contained" color="error">
            Permanently Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriversPage;
